import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AcknowledgedPolicyEnum } from "@/core/constants";
import { AlertBar2 } from "@/ui2/atoms/AlertBar";
import { BillingSupportInfo } from "@/components/Consent/BillingSupportInfo";
import { Button2 } from "@/ui2/atoms/Button";
import Captcha from "@/components/Common/Captcha/Captcha";
import { Card2 } from "@/ui2/atoms/Card";
import { createAcknowledgementV2 } from "@/utils/acknowledgementUtils";
import { CreditCardAgreementScrollableTextModalHOC } from "@/component-library/hocs/CreditCardAgreementScrollableTextModalHOC";
import { CrisisSupportInfo } from "@/components/Consent/CrisisSupportInfo";
import dayjs from "@/lib/helpers/dayjs";
import { DEFAULT_TOAST_TIMEOUT } from "@/lib/constants/delay";
import { displayElavonCreditCardModal, parsePaymentResponse } from "@/utils/PaymentProvider/elavon";
import {
  fetchPatientPaywall,
  putPatientCardInfo,
  updatePatientPaywallInfo,
} from "@/redux/actions/patientCardInfo";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { Heading2 } from "@/ui2/atoms/Heading";
import { hideLoader, showLoader } from "@/redux/actions/loader";
import { patientRequests } from "@/api/(requests)/patient";
import { profileFetchSuccess } from "@/redux/actions/auth";
import { selectPatientCardInfo } from "@/redux/selectors/patientCardInfoSelectors";
import { selectProfile } from "@/redux/selectors/authSelectors";
import { showToast } from "@/providers/ToastProvider";
import { testLocator } from "@/lib/helpers/test-locator";
import { tryCatch } from "@/lib/helpers/tryCatch";

import "./PaymentPaywall.styles.scss";

const PAYMENT_ERROR_MESSAGE = "Your card couldn't be added. Please try again.";
const WORKFLOW = "PAYWALL";

export function PaymentPaywall({ onPaywallCompleted }) {
  const dispatch = useDispatch();
  const profile = useSelector(selectProfile);
  const { isPaymentUpdateRequired, isSoftPaywall, lastFailedChargeDate } =
    useSelector(selectPatientCardInfo);

  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [captchaVerificationToken, setCaptchaVerificationToken] = useState(false);

  const { acknowledgements, first_name, last_name, username } = profile;

  const onBackendError = () => {
    showToast(backendErrorToast);
    onPaywallCompleted();
  };

  const onCancelled = () => setErrorMessage(PAYMENT_ERROR_MESSAGE);
  const onDeclined = () => setErrorMessage(PAYMENT_ERROR_MESSAGE);
  const onError = () => setErrorMessage(PAYMENT_ERROR_MESSAGE);

  const onApproval = (response) => {
    dispatch(
      putPatientCardInfo({
        username: username,
        ...parsePaymentResponse(response),
        onErrorCallback: onBackendError,
      }),
    );

    onPaywallCompleted();
    showToast(successToast);
  };

  const closeModal = () => {
    setShowModal(false);
    setCaptchaVerificationToken(null);
  };

  const handleElavonCreditCardModalException = (e) => {
    showToast(errorToast);
    // Allow user to temporarily progress if error is outside their control
    if (!e?.code || e?.code >= 500) {
      showToast(errorToast);

      onPaywallCompleted();
      dispatch(updatePatientPaywallInfo({ showPaywall: false }));
    }

    throw e;
  };

  const onCreditCardTncSubmit = async () => {
    dispatch(showLoader());

    const acknowledgementsData = {
      acknowledgements: {
        ...acknowledgements,
        [AcknowledgedPolicyEnum.CREDIT_CARD_AUTHORIZATION_POLICY_NAME]: createAcknowledgementV2({
          consentResponse: true,
        }),
      },
      username,
    };

    dispatch(hideLoader());
    dispatch(profileFetchSuccess({ ...profile, ...acknowledgementsData }));

    const [_profileRes, profileError] = await tryCatch(
      patientRequests.update_profile({ data: acknowledgementsData }),
    );

    if (profileError) {
      showToast(errorToast);

      return;
    }

    closeModal();

    const [_elavonRes, elavonError] = await tryCatch(
      displayElavonCreditCardModal(
        dispatch,
        username,
        first_name,
        last_name,
        () => {}, // setErrorMessage
        WORKFLOW,
        onError,
        onCancelled,
        onDeclined,
        onApproval,
        false,
        captchaVerificationToken,
      ),
    );

    if (elavonError) handleElavonCreditCardModalException(elavonError);

    setCaptchaVerificationToken(null);
  };

  useEffect(() => {
    if (isSoftPaywall === undefined) {
      dispatch(fetchPatientPaywall());
    }

    async function updateProfile() {
      const [_response, error] = await tryCatch(
        patientRequests.update_profile({
          data: { ...profile, soft_paywall: Date.now() },
        }),
      );

      if (error) showToast(errorToast);
    }

    if (isSoftPaywall) updateProfile();
  }, [dispatch, isSoftPaywall, profile]);

  const captchaComponent = <Captcha setCaptchaVerificationToken={setCaptchaVerificationToken} />;

  return (
    <>
      <CreditCardAgreementScrollableTextModalHOC
        captchaCompleted={captchaVerificationToken}
        captchaComponent={captchaComponent}
        onCancel={closeModal}
        onSubmit={onCreditCardTncSubmit}
        show={showModal}
      />

      <Card2
        className="tw:mx-auto tw:flex-col tw:gap-8 tw:p-12 tw:lg:max-w-146"
        {...getAutomationDataAttr("payment-paywall-card")}
        {...testLocator("paymentPaywallCard")}
      >
        <Heading2 as="h1" className="tw:text-center" size={4}>
          {isPaymentUpdateRequired ? "Update payment method" : "Add a payment method"}
        </Heading2>

        {errorMessage && (
          <AlertBar2 variant="error">
            <b>{errorMessage}</b>
          </AlertBar2>
        )}

        {isPaymentUpdateRequired && (
          <>
            {lastFailedChargeDate && (
              <p className="body-md">
                We were unable to charge your card on file on
                <time className="tw:ml-0.5ch" dateTime={dayjs(lastFailedChargeDate).toISOString()}>
                  {dayjs(lastFailedChargeDate).format("MMM D, YYYY")}
                </time>
                .
              </p>
            )}

            <p className="body-md">
              If your card on file is disabled or expired, please update your payment information.
              If your card on file is still valid and you've resolved processing issues with your
              bank, please add the card again.
            </p>
          </>
        )}

        {!isPaymentUpdateRequired && (
          <p className="body-md">
            Based on your benefits, you may have additional costs associated with your care. Adding
            a payment method to your account is required to connect with a provider.
          </p>
        )}

        <Button2
          className="tw:mx-auto! tw:min-w-50"
          onPress={() => {
            window.grecaptcha.reset();
            setShowModal(true);
          }}
          size="md"
          {...testLocator("paymentPaywallAddCardBtn")}
        >
          {isPaymentUpdateRequired ? "Update Card" : "Add Card"}
        </Button2>

        {isSoftPaywall && (
          <p className="body-sm" style={{ color: "var(--brand-gray-600) !important" }}>
            Don’t have your card handy?
            <Button2
              className="tw:mx-0.5ch!"
              onPress={onPaywallCompleted}
              variant="link"
              {...getAutomationDataAttr("payment-paywall-skip")}
              {...testLocator("paymentPaywallSkip")}
            >
              Skip this step.
            </Button2>
            Next time you sign in, your account will be locked until you add a payment method.
          </p>
        )}

        <hr className="tw:my-0!" />

        <CrisisSupportInfo />
        <BillingSupportInfo />
      </Card2>
    </>
  );
}

const successToast = [
  { heading: "New card added.", variant: "success" },
  { timeout: DEFAULT_TOAST_TIMEOUT },
];

const backendErrorToast = [
  {
    heading: "Error adding card on file: Next time you sign in, we'll ask you to try again.",
    variant: "error",
  },
  { timeout: DEFAULT_TOAST_TIMEOUT },
];

const errorToast = [
  {
    heading:
      "Unexpected error adding card on file: Next time you sign in, we'll ask you to try again.",
    variant: "error",
  },
  { timeout: DEFAULT_TOAST_TIMEOUT },
];
